import React, { useState } from "react";
import { adminApi } from "../api";

/**
 * Admin config for FOXID driver-licence capture (per venue). Credentials +
 * which flows offer the capture step. Mirrors the other integration config
 * cards. Works for both IGT and S7000 venues — for IGT the scan also fills
 * the identification block; for any venue it fills name/DOB/address.
 */
export default function FoxidConfigTab({ venueId, initial }) {
  const [form, setForm] = useState({
    base_url:     initial?.base_url     || "",
    api_username: initial?.api_username || "",
    api_password: initial?.api_password || "",
    enabled:      !!initial?.enabled,
    on_signup:    initial?.on_signup    !== false,
    on_renewal:   initial?.on_renewal   !== false,
    face_match:   !!initial?.face_match,
  });
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState(null); // { ok, text }

  const set = (k) => (e) => setForm((p) => ({ ...p, [k]: e.target.value }));
  const toggle = (k) => (e) => setForm((p) => ({ ...p, [k]: e.target.checked }));

  const save = async (e) => {
    e.preventDefault();
    setSaving(true); setMsg(null);
    try {
      await adminApi.updateFoxidConfig(venueId, form);
      setMsg({ ok: true, text: "FOXID configuration saved." });
    } catch (err) {
      setMsg({ ok: false, text: err.message || "Save failed" });
    } finally {
      setSaving(false);
    }
  };

  const ToggleRow = ({ field, title, sub }) => (
    <label className="toggle-row" htmlFor={`foxid_${field}`}>
      <div className="toggle-row-text">
        <strong>{title}</strong>
        <span>{sub}</span>
      </div>
      <div className="toggle-switch">
        <input type="checkbox" id={`foxid_${field}`} checked={!!form[field]} onChange={toggle(field)} />
        <span className="toggle-track" />
      </div>
    </label>
  );

  return (
    <div style={{ maxWidth: 720 }}>
      <h3 style={{ margin: "0 0 0.25rem" }}>Driver Licence Capture</h3>
      <p style={{ color: "var(--text-muted)", marginTop: 0, fontSize: "0.9rem", lineHeight: 1.5 }}>
        Let members photograph their driver licence during signup or renewal. We send it to
        FOXID, which reads their details and pre-fills the form — no manual typing. Each venue
        uses its own FOXID credentials.
      </p>

      <form onSubmit={save} style={{ border: "1px solid var(--border)", borderRadius: 12, padding: "1.25rem", marginTop: "1rem" }}>
        <div className="form-section-label">Credentials</div>
        <div className="form-field">
          <label>Base URL</label>
          <input value={form.base_url} onChange={set("base_url")} placeholder="https://your-foxid-host" />
        </div>
        <div className="form-grid-2">
          <div className="form-field">
            <label>API Username</label>
            <input value={form.api_username} onChange={set("api_username")} autoComplete="off" />
          </div>
          <div className="form-field">
            <label>API Password</label>
            <input type="password" value={form.api_password} onChange={set("api_password")} autoComplete="new-password" />
          </div>
        </div>

        <div className="form-section-label" style={{ marginTop: "1rem" }}>Where to offer it</div>
        <ToggleRow field="enabled"    title="Enable licence capture"  sub="Master switch. Off = never shown, even if creds are set." />
        <ToggleRow field="on_signup"  title="On new signups"          sub="Show the capture step in the new-member flow." />
        <ToggleRow field="on_renewal" title="On renewals"             sub="Show the capture step when an existing member renews." />

        <div className="form-section-label" style={{ marginTop: "1rem" }}>Face match</div>
        <ToggleRow field="face_match" title="Selfie face match" sub="Add a selfie step after the licence. Needs licence capture enabled." />
        <p style={{ color: "var(--text-muted)", margin: "0.4rem 0 0", fontSize: "0.82rem", lineHeight: 1.5 }}>
          The selfie is its own step, captured even when the member enters details manually. If they scanned a licence we match the selfie to the licence photo (FOXID Face_Match); otherwise the selfie is kept with no match. The outcome — matched, no match, not performed or skipped — is recorded on the member's document and shown in both portals. It never blocks the join.
        </p>

        <div className="info-box" style={{ marginTop: "1rem" }}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="16" x2="12" y2="12"/><line x1="12" y1="8" x2="12.01" y2="8"/></svg>
          <span>The capture step only appears to members once this is enabled <em>and</em> the credentials above are filled in. Members can always skip it and type their details manually.</span>
        </div>

        <div className="form-actions" style={{ marginTop: "1rem" }}>
          <button type="submit" className="btn btn-primary" disabled={saving}>{saving ? "Saving…" : "Save FOXID config"}</button>
        </div>

        {msg && (
          <div className={`info-box ${msg.ok ? "" : "error"}`} style={{ marginTop: "0.75rem", color: msg.ok ? "var(--success)" : "var(--danger)" }}>
            <span>{msg.text}</span>
          </div>
        )}
      </form>
    </div>
  );
}
